import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Lab-test fuzzy search for the Add-investigation editor.
 *
 * REWIRED (2026-07-31): this used to call api-lab-investigation-v2-service's
 * federated `/labtest-catalog`, which is NOT DEPLOYED anywhere (no gitops dir,
 * no ingress; dev points it at localhost:8088) — so the dropdown was silently
 * dead. It now composes the same live api-labtest-service endpoints Create Rx's
 * popover uses:
 *
 *   GET /dt/labtestmapping/search?queryString=&isLabTest=false
 *       the synonym map — matches aliases ("Hb") AND most canonical names, and
 *       is the only source carrying the PARENT panel code and the UNIT.
 *   GET /dt/labtest/search/subtest/name?queryString=
 *       the catalogue's own sub-test search — canonical names the synonym map
 *       missed, but its projection DROPS the parent, so those rows arrive
 *       panel-less (still pickable; panel just stays blank).
 *   POST /dt/labtest {ltCodes:[…]}
 *       one bulk call resolving panel codes to CANONICAL names + children.
 *       Load-bearing twice over: the synonym map's `mainTestName` is stale in
 *       production (LABT284 renders as a different panel entirely), and its
 *       `label` is the ALIAS that matched, not the test's real name — both are
 *       corrected against the catalogue here, never displayed raw.
 *
 * Output contract is unchanged from the dead service, so the autocomplete and
 * the editor need no new parsing: `[{ sltCode, ltCode, name, panel, unit,
 * refRange }]`. `refRange` is ALWAYS `{}` — no service in the estate stores
 * reference ranges (the MySQL catalogue has no such columns, the synonym map
 * neither), so the range stays a manual field however "auto" the board's
 * caption says it is.
 */
class LabTestCatalogService {
  constructor() {
    this.apiService = new APIService();
  }

  /**
   * One-shot fetch of the full labtest + subtest catalog. Used by the
   * Digitise-manually modal to prime a picker corpus at open time so
   * every keystroke fuzzy-search stays client-side. Sorted ascending by
   * testName before the callback fires.
   *
   * Response is the raw upstream shape (see APIConfig.labTest.withSubTestGetAll).
   * Callbacks receive an array — empty on failure so the caller can render
   * a "catalog unavailable" state without a second branch.
   */
  getAllWithSubTests(onResponse, onError) {
    return this.apiService.get(
      APIConfig.labTest.withSubTestGetAll,
      (res) => {
        const list = Array.isArray(res) ? res.slice() : [];
        list.sort((a, b) =>
          String(a.testName || "").localeCompare(String(b.testName || "")),
        );
        // Nested subtests also alphabetised so the Sub-test picker order
        // stays predictable regardless of upstream ordering.
        list.forEach((t) => {
          if (Array.isArray(t.subtest)) {
            t.subtest = t.subtest
              .slice()
              .sort((a, b) =>
                String(a.subtestName || "").localeCompare(String(b.subtestName || "")),
              );
          }
        });
        onResponse(list);
      },
      (err) => {
        if (typeof onError === "function") onError(err);
        else onResponse([]);
      },
    );
  }

  /**
   * Sub-test on-select detail — fires when the reviewer picks a sub-test
   * in the Digitise-manually modal. The response carries the canonical
   * `loincName` (long-form label), which the picker corpus / search
   * endpoints don't. Callback receives `{ loincName, loincCode, ... }`
   * (raw upstream shape); non-fatal on failure (caller falls back to a
   * blank LOINC display).
   */
  getSubTestOnSelect(ltCode, sltCode, onResponse, onError) {
    if (!ltCode || !sltCode) {
      onResponse(null);
      return undefined;
    }
    const url = APIConfig.labTest.subTestOnSelect.format(ltCode, sltCode);
    return this.apiService.get(
      url,
      (res) => onResponse(res || null),
      (err) => {
        if (typeof onError === "function") onError(err);
        else onResponse(null);
      },
    );
  }

  search(queryString, onResponse, onError) {
    const q = encodeURIComponent((queryString || "").trim());
    if (!q) {
      onResponse([]);
      return undefined;
    }

    let synonyms = null;
    let canonical = null;

    const both = () => {
      if (synonyms === null || canonical === null) return;

      // Join by SLABT code. Synonym rows contribute panel + unit; the
      // canonical search contributes the display name.
      const byCode = {};
      const order = [];
      const remember = (code, patch) => {
        if (!code) return;
        if (!byCode[code]) {
          byCode[code] = { sltCode: code, ltCode: "", name: "", panel: "", unit: "", refRange: {} };
          order.push(code);
        }
        Object.keys(patch).forEach((k) => {
          if (patch[k] && !byCode[code][k]) byCode[code][k] = patch[k];
        });
      };

      canonical.forEach((r) => r && remember(r.value, { name: r.label }));
      synonyms.forEach(
        (r) =>
          r &&
          remember(r.value, {
            // The alias only fills in where the canonical search had no name —
            // it is what MATCHED, not necessarily what the test is called.
            name: r.label,
            ltCode: r.mainTestCode,
            unit: String(r.unit || "").trim(),
          })
      );

      const rows = order.map((c) => byCode[c]);
      const ltCodes = [...new Set(rows.map((r) => r.ltCode).filter(Boolean))];
      if (!ltCodes.length) {
        onResponse(rows);
        return;
      }

      // Resolve panel display names AND canonical sub-test names from the
      // catalogue itself. postAgain: post()'s 401 retry would double-fire a
      // pure read.
      this.apiService.postAgain(
        APIConfig.labTest.childrenBulk,
        { ltCodes },
        (res) => {
          const panelName = {};
          const testName = {};
          (Array.isArray(res) ? res : []).forEach((p) => {
            if (!p || !p.ltCode) return;
            panelName[p.ltCode] = p.testName || "";
            (p.subtest || []).forEach((s) => {
              const code = s && (s.sltCode || s.sltcode);
              // `subtestName`, lowercase t — the camelCase key is silently blank.
              if (code) testName[code] = s.subtestName || s.subTestName || "";
            });
          });
          onResponse(
            rows.map((r) => ({
              ...r,
              name: testName[r.sltCode] || r.name,
              panel: panelName[r.ltCode] || "",
            }))
          );
        },
        // The catalogue being unreachable degrades to alias names + no panel
        // rather than an empty dropdown.
        () => onResponse(rows)
      );
    };

    this.apiService.get(
      APIConfig.labTestMapping.search.format(q),
      (res) => {
        synonyms = Array.isArray(res) ? res : [];
        both();
      },
      () => {
        synonyms = [];
        both();
      }
    );
    this.apiService.get(
      APIConfig.labTest.subTestByName.format(q),
      (res) => {
        canonical = Array.isArray(res) ? res : [];
        both();
      },
      () => {
        canonical = [];
        both();
      }
    );
    return undefined;
  }
}

export default LabTestCatalogService;
